import { DollarSign, AlertTriangle } from 'lucide-react';
import type { Budget } from '../../../../../types/budget';

interface BudgetSummaryProps {
  budget: Budget;
}

export function BudgetSummary({ budget }: BudgetSummaryProps) {
  const totalExpenses = budget.expenses.reduce((sum, expense) => sum + expense.amount, 0);
  const remainingBudget = budget.totalAmount - totalExpenses;
  const percentageUsed = (totalExpenses / budget.totalAmount) * 100;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-800">{budget.eventName}</h2>
        <div className="text-sm text-gray-500">
          {new Date(budget.startDate).toLocaleDateString()} - {new Date(budget.endDate).toLocaleDateString()}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-indigo-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="text-indigo-600">
              <DollarSign className="h-6 w-6" />
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Total Budget</p>
              <p className="text-lg font-semibold text-gray-900">{formatCurrency(budget.totalAmount)}</p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="text-green-600">
              <DollarSign className="h-6 w-6" />
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Spent</p>
              <p className="text-lg font-semibold text-gray-900">{formatCurrency(totalExpenses)}</p>
            </div>
          </div>
        </div>

        <div className={`${remainingBudget < 0 ? 'bg-red-50' : 'bg-blue-50'} p-4 rounded-lg`}>
          <div className="flex items-center justify-between">
            <div className={remainingBudget < 0 ? 'text-red-600' : 'text-blue-600'}>
              {remainingBudget < 0 ? (
                <AlertTriangle className="h-6 w-6" />
              ) : (
                <DollarSign className="h-6 w-6" />
              )}
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Remaining</p>
              <p className="text-lg font-semibold text-gray-900">{formatCurrency(remainingBudget)}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="flex justify-between text-sm text-gray-500 mb-2">
          <span>Budget Usage</span>
          <span>{percentageUsed.toFixed(1)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div
            className={`h-2.5 rounded-full ${
              percentageUsed >= 90 ? 'bg-red-600' : percentageUsed >= 75 ? 'bg-yellow-400' : 'bg-green-600'
            }`}
            style={{ width: `${Math.min(percentageUsed, 100)}%` }}
          ></div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
        {Object.entries(budget.allocations).map(([category, amount]) => {
          const categoryExpenses = budget.expenses
            .filter(expense => expense.category === category)
            .reduce((sum, expense) => sum + expense.amount, 0);
          const categoryPercentage = (categoryExpenses / amount) * 100;

          return (
            <div key={category} className="bg-gray-50 p-3 rounded-lg">
              <div className="text-sm font-medium text-gray-700 mb-1">{category}</div>
              <div className="text-xs text-gray-500 mb-2">
                {formatCurrency(categoryExpenses)} / {formatCurrency(amount)}
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div
                  className={`h-1.5 rounded-full ${
                    categoryPercentage >= 90 ? 'bg-red-600' : categoryPercentage >= 75 ? 'bg-yellow-400' : 'bg-green-600'
                  }`}
                  style={{ width: `${Math.min(categoryPercentage, 100)}%` }}
                ></div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}